import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomers } from "../../../store/Slices/CustomerSlice";
import { fetchCustomerInvoice } from "../../../store/Slices/CustomerInvoiceSlice";
import {
  ErrorToast,
  SuccessToast,
  WarningToast,
} from "../../../utils/ShowToast";
import CustomerSelector from "../../../components/Selector/CustomerSelector";
import CreateInvoiceItemsTable, {
  getCommittedEditInvoiceItems,
  getCommittedInvoiceItems,
  mapInvoiceProductsToItemRows,
} from "../../../components/InoviceAddItem/CreateInvoiceItemsTable";
import {
  GetCustomerInvoiceSingleApi,
  UpdateCustomerInvoiceDetailsApi,
} from "../../../ApiRequests";
import SaleType from "../../../components/Input/SaleType";
import InvoiceCustomerType from "../../../components/Input/InvoiceCustomerType";
import FetchingLoading from "../../../components/Loaders/FetchingLoading";
import ThermalPrintModeSelect from "../../../components/Thermal/ThermalPrintModeSelect";
import ThermalCustomerInvoice from "../../../components/Reports/ThermalCustomerInvoice";
import {
  FiAlertCircle,
  FiPackage,
  FiPrinter,
  FiSend,
  FiUser,
  FiXCircle,
} from "react-icons/fi";
import moment from "moment";
import { getAppBasePath } from "../../../utils/appPaths";
import {
  syncEditDocumentItems,
  isGuestInvoice,
} from "../../../utils/invoice";
import { fetchProducts } from "../../../store/Slices/ProductSlice";
import { toLocalYmd } from "../../../utils/dateTime";

const userData = JSON.parse(localStorage.getItem("user")) || {};

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const defaultThermalPrintMode = (guest) =>
  guest ? "hideItemPrices" : "qtyOnly";

const PosField = ({ label, children, className = "" }) => (
  <label className={`flex flex-col gap-1 min-w-0 ${className}`}>
    <span className="text-[10px] font-bold uppercase tracking-wider text-muted">
      {label}
    </span>
    {children}
  </label>
);

const posInputClass =
  "w-full h-10 px-3 text-sm font-semibold border border-line rounded-lg outline-none bg-card text-ink placeholder:text-muted focus:border-main focus:ring-2 focus:ring-main/15";

const InvoiceMetaFields = ({
  invoiceNoInputRef,
  invoiceNo,
  onInvoiceNoChange,
  date,
  onDateChange,
  description,
  onDescriptionChange,
  autoInvoiceNo,
  isGuest = false,
}) => {
  const descriptionInputRef = useRef(null);

  return (
    <>
      <PosField label="Invoice #">
        <input
          ref={invoiceNoInputRef}
          type="number"
          value={invoiceNo}
          onChange={(e) => onInvoiceNoChange(e.target.value)}
          placeholder="Auto"
          className={posInputClass}
        />
        {autoInvoiceNo != null && (
          <span className="text-[10px] text-muted mt-0.5">
            System #{" "}
            <span className="font-semibold text-main">{autoInvoiceNo}</span>
          </span>
        )}
      </PosField>
      <PosField label="Date">
        <input
          type="date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Tab") return;
            e.preventDefault();
            if (e.shiftKey) {
              invoiceNoInputRef?.current?.focus({ preventScroll: true });
            } else {
              descriptionInputRef.current?.focus({ preventScroll: true });
            }
          }}
          className={posInputClass}
          title="Click to change date · Tab to continue"
        />
      </PosField>
      <PosField label={isGuest ? "Description (optional)" : "Description"}>
        <input
          ref={descriptionInputRef}
          type="text"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={
            isGuest
              ? "Optional note"
              : "Required — e.g. Marka / note"
          }
          className={posInputClass}
        />
      </PosField>
    </>
  );
};

const SummaryLine = ({ label, value, highlight, bold, large }) => (
  <div
    className={`flex justify-between items-baseline gap-3 ${
      bold ? "pt-3 mt-2 border-t-2 border-main" : "py-1.5"
    }`}
  >
    <span
      className={`${
        bold
          ? "text-sm font-bold text-ink"
          : "text-xs text-muted uppercase tracking-wide"
      }`}
    >
      {label}
    </span>
    <span
      className={`tabular-nums font-bold ${
        large
          ? "text-2xl text-main"
          : bold
          ? "text-lg text-main"
          : "text-sm text-ink"
      } ${highlight ? "text-success" : ""}`}
    >
      {value}
    </span>
  </div>
);

const EditInvoice = () => {
  const { invoiceId: routeInvoiceId } = useParams();
  const navigate = useNavigate();

  const goBackFromEdit = useCallback(() => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(`${getAppBasePath()}/invoices`);
  }, [navigate]);
  const dispatch = useDispatch();
  const ItemsState = useSelector((state) => state.ItemsState);

  const saleType = "SALE";

  const [SelectedCustomer, setSelectedCustomer] = useState("");
  const [OriginalCustomerId, setOriginalCustomerId] = useState("");
  const [invoiceCustomerType, setInvoiceCustomerType] = useState("CUSTOMER");
  const [guestName, setGuestName] = useState("");
  const [guestContact, setGuestContact] = useState("");
  const [guestAddress, setGuestAddress] = useState("");
  const [Payment, setPayment] = useState("");
  const [itemRows, setItemRows] = useState([]);
  const [Discount, setDiscount] = useState(0);
  const [CurDate, setCurDate] = useState("");
  const [InvoiceNo, setInvoiceNo] = useState("");
  const [Description, setDescription] = useState("");
  const [Loading, setLoading] = useState(false);
  const [pageReady, setPageReady] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [autoInvoiceNo, setAutoInvoiceNo] = useState(null);

  const [thermalReceiptData, setThermalReceiptData] = useState(null);
  const [thermalPrintMode, setThermalPrintMode] = useState("qtyOnly");
  const [itemsSourceKey, setItemsSourceKey] = useState(0);
  const [customerSelectorOpenTrigger, setCustomerSelectorOpenTrigger] =
    useState(0);

  const invoiceNoInputRef = useRef(null);
  const guestContactRef = useRef(null);
  const guestAddressRef = useRef(null);
  const discountRef = useRef(null);
  const paymentRef = useRef(null);
  const thermalPrintRef = useRef(null);
  const silentPrintPendingRef = useRef(false);
  const hydratedItemsKeyRef = useRef("");
  const loadingRef = useRef(false);
  const lastEnterAtRef = useRef(0);
  const submitRef = useRef(null);
  const DOUBLE_ENTER_MS = 450;

  loadingRef.current = Loading;

  const isGuest =
    invoiceCustomerType === "GUEST" || invoiceCustomerType === "WALK_IN";

  const committedItems = useMemo(
    () => getCommittedInvoiceItems(itemRows),
    [itemRows]
  );

  const billTotal = useMemo(
    () => committedItems.reduce((t, i) => t + Number(i.amount || 0), 0),
    [committedItems]
  );

  const purchaseTotal = useMemo(
    () =>
      committedItems.reduce(
        (t, i) => t + Number(i.purchase || 0) * Number(i.qty || 0),
        0
      ),
    [committedItems]
  );

  const totalQty = useMemo(
    () => committedItems.reduce((t, i) => t + Number(i.qty || 0), 0),
    [committedItems]
  );

  const shoppingTotal = useMemo(
    () =>
      committedItems
        .filter((dt) => dt.unit === "SH")
        .reduce((t, i) => t + Number(i.amount || 0), 0),
    [committedItems]
  );

  const netBill = useMemo(
    () => Math.max(0, billTotal - Number(Discount || 0)),
    [billTotal, Discount]
  );

  const profit = billTotal - purchaseTotal;

  const oldBalanceExcludingInvoice = useMemo(() => {
    if (!SelectedCustomer || !invoice || isGuest) return 0;
    const receivable = Number(SelectedCustomer.recieveable) || 0;
    const invoiceNet =
      Number(invoice.total_amount || 0) - Number(invoice.payment || 0);
    const same =
      String(OriginalCustomerId) === String(SelectedCustomer._id);
    if (!same) return receivable;
    return receivable - invoiceNet;
  }, [SelectedCustomer, invoice, OriginalCustomerId, isGuest]);

  const remaining = useMemo(() => {
    if (!SelectedCustomer || isGuest) return 0;
    return (
      oldBalanceExcludingInvoice +
      billTotal -
      Number(Payment || 0) -
      Number(Discount || 0)
    );
  }, [
    SelectedCustomer,
    oldBalanceExcludingInvoice,
    billTotal,
    Payment,
    Discount,
    isGuest,
  ]);

  const customerChanged = useMemo(() => {
    if (isGuest || !OriginalCustomerId || !SelectedCustomer?._id) return false;
    return String(OriginalCustomerId) !== String(SelectedCustomer._id);
  }, [OriginalCustomerId, SelectedCustomer, isGuest]);

  const receiptCustomer = useMemo(() => {
    if (isGuest) {
      return {
        name: guestName.trim(),
        contact: guestContact,
        address: guestAddress,
      };
    }
    if (!SelectedCustomer) return null;
    return {
      ...SelectedCustomer,
      contact: guestContact || SelectedCustomer.contact,
      address: guestAddress || SelectedCustomer.address,
    };
  }, [SelectedCustomer, guestContact, guestAddress, guestName, isGuest]);

  const focusInvoiceNoField = useCallback(() => {
    invoiceNoInputRef.current?.focus({ preventScroll: true });
  }, []);

  const focusFirstItemCode = useCallback(() => {
    const input = document.querySelector(
      "[data-invoice-items-table] tbody tr:first-child input"
    );
    input?.focus({ preventScroll: true });
  }, []);

  const applyInvoicePayload = useCallback((data) => {
    if (!data) return;
    setInvoice(data);
    setInvoiceNo(data.invoice_no || "");
    setDescription(data.desc || "");
    setCurDate(
      data.date
        ? moment.unix(data.date).format("YYYY-MM-DD")
        : toLocalYmd()
    );
    setDiscount(data.discount ?? 0);
    setPayment(data.payment ?? "");
    setAutoInvoiceNo(data.auto_invoice_no ?? null);
    setOriginalCustomerId(data.customerId?._id || data.customerId || "");
    const guest = isGuestInvoice(data);
    const type =
      data.invoice_customer_type === "GUEST" ||
      data.invoice_customer_type === "WALK_IN"
        ? data.invoice_customer_type
        : guest
          ? "GUEST"
          : "CUSTOMER";
    setInvoiceCustomerType(type);
    setGuestName(
      String(data.guest_name || data.walk_in_name || "").trim()
    );
    setGuestContact(data.guest_contact || data.walk_in_contact || "");
    setGuestAddress(data.guest_address || data.walk_in_address || "");
    setThermalPrintMode(defaultThermalPrintMode(guest || type === "GUEST"));
    if (!guest && data.customerId && typeof data.customerId === "object") {
      setSelectedCustomer(data.customerId);
    } else {
      setSelectedCustomer("");
    }
    setItemsSourceKey((key) => key + 1);
  }, []);

  const refreshInvoice = useCallback(async () => {
    if (!routeInvoiceId) return;
    const response = await GetCustomerInvoiceSingleApi(routeInvoiceId);
    const data = response?.data?.data?.payload;
    if (!data) throw new Error("not found");
    applyInvoicePayload(data);
    return data;
  }, [routeInvoiceId, applyInvoicePayload]);

  const handleSilentThermalPrint = useReactToPrint({
    content: () => thermalPrintRef.current,
    documentTitle: `Invoice-${thermalReceiptData?.invoice_no || "receipt"}`,
    removeAfterPrint: true,
    onAfterPrint: () => {
      silentPrintPendingRef.current = false;
      setThermalReceiptData(null);
      goBackFromEdit();
    },
  });

  const queueSilentPrint = (receipt) => {
    silentPrintPendingRef.current = true;
    setThermalReceiptData(receipt);
  };

  const buildReceiptForPrint = useCallback(
    (savedInvoice) => {
      const stock = Array.isArray(ItemsState.data) ? ItemsState.data : [];
      const items =
        savedInvoice?.items?.length > 0
          ? getCommittedInvoiceItems(
              mapInvoiceProductsToItemRows(savedInvoice.items, stock)
            )
          : committedItems;
      const lineTotal = items.reduce(
        (t, i) => t + Number(i.amount || 0),
        0
      );
      const discountVal = Number(savedInvoice?.discount ?? Discount) || 0;
      const paymentVal = Number(savedInvoice?.payment ?? Payment) || 0;

      return {
        customer: receiptCustomer,
        invoice_no:
          (savedInvoice?.invoice_no && String(savedInvoice.invoice_no).trim()) ||
          InvoiceNo.trim() ||
          "",
        auto_invoice_no:
          savedInvoice?.auto_invoice_no ?? autoInvoiceNo ?? "",
        date: savedInvoice?.date
          ? moment.unix(savedInvoice.date).format("YYYY-MM-DD")
          : CurDate,
        desc: savedInvoice?.desc ?? Description,
        items,
        discount: discountVal,
        payment: paymentVal,
        billTotal: lineTotal,
        oldBalance: oldBalanceExcludingInvoice,
        remaining,
        saleType,
        invoiceCustomerType,
        printMode: thermalPrintMode,
      };
    },
    [
      ItemsState.data,
      committedItems,
      receiptCustomer,
      InvoiceNo,
      autoInvoiceNo,
      CurDate,
      Description,
      Discount,
      Payment,
      oldBalanceExcludingInvoice,
      remaining,
      invoiceCustomerType,
      thermalPrintMode,
    ]
  );

  useEffect(() => {
    if (!silentPrintPendingRef.current || !thermalReceiptData) return;
    const timer = setTimeout(() => {
      silentPrintPendingRef.current = false;
      handleSilentThermalPrint();
    }, 350);
    return () => clearTimeout(timer);
  }, [thermalReceiptData, handleSilentThermalPrint]);

  useEffect(() => {
    dispatch(fetchCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (!routeInvoiceId) {
      navigate(`${getAppBasePath()}/invoices`, { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await refreshInvoice();
        if (!cancelled) setPageReady(true);
      } catch {
        if (!cancelled) {
          ErrorToast("Failed to load invoice");
          navigate(`${getAppBasePath()}/invoices`, { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeInvoiceId, navigate, refreshInvoice]);

  useEffect(() => {
    if (!invoice || !itemsSourceKey) return;
    const stock = Array.isArray(ItemsState.data) ? ItemsState.data : [];
    const hasStock = stock.length > 0;
    const hydrateKey = `${invoice._id}:${itemsSourceKey}:${
      hasStock ? "stock" : "nostock"
    }`;
    if (hydratedItemsKeyRef.current === hydrateKey) return;
    if (
      hydratedItemsKeyRef.current.startsWith(
        `${invoice._id}:${itemsSourceKey}:`
      ) &&
      hydratedItemsKeyRef.current.endsWith(":stock")
    ) {
      return;
    }
    hydratedItemsKeyRef.current = hydrateKey;
    setItemRows(mapInvoiceProductsToItemRows(invoice.items || [], stock));
  }, [invoice, itemsSourceKey, ItemsState.data]);

  useEffect(() => {
    if (SelectedCustomer && !isGuest) {
      setGuestContact(SelectedCustomer.contact || "");
      setGuestAddress(SelectedCustomer.address || "");
    }
  }, [SelectedCustomer, isGuest]);

  useEffect(() => {
    if (isGuest && pageReady) {
      setPayment(String(netBill));
    }
  }, [isGuest, netBill, pageReady]);

  const onSubmit = async (e, type = 1) => {
    e?.preventDefault?.();
    setLoading(true);

    if (!isGuest && !SelectedCustomer) {
      WarningToast("Select Customer!");
    } else if (isGuest && !guestName.trim()) {
      WarningToast("Enter Guest Name!");
    } else if (!isGuest && !Description.trim()) {
      WarningToast("Enter Description!");
    } else if (!CurDate) {
      WarningToast("Enter a valid Date");
    } else if (!committedItems.length) {
      WarningToast("Add at least one item!");
    } else if (isGuest && Number(Payment || 0) < netBill) {
      WarningToast("Guest sale requires full cash payment!");
    } else {
      try {
        const editRows = getCommittedEditInvoiceItems(itemRows);

        await syncEditDocumentItems({
          documentId: routeInvoiceId,
          isReturn: false,
          accountCustomerId: isGuest ? undefined : OriginalCustomerId,
          originalItems: invoice?.items || [],
          committedRows: editRows,
        });

        const response = await UpdateCustomerInvoiceDetailsApi(routeInvoiceId, {
          ...(isGuest
            ? {
                guest_name: guestName.trim(),
                guest_contact: guestContact,
                guest_address: guestAddress,
              }
            : { customerId: SelectedCustomer._id }),
          invoice_no: InvoiceNo.trim(),
          desc: Description.trim(),
          date: CurDate,
          discount: Number(Discount) || 0,
          payment: Number(Payment) || 0,
          branchId:
            invoice?.branchId?._id ||
            invoice?.branchId ||
            userData.branchId,
        });

        if (response.data.success) {
          SuccessToast("Invoice updated successfully");
          dispatch(fetchCustomers());
          dispatch(fetchCustomerInvoice());
          dispatch(fetchProducts());

          const saved = response.data?.data?.payload;

          if (type === 2) {
            queueSilentPrint(buildReceiptForPrint(saved));
          } else {
            await refreshInvoice();
            goBackFromEdit();
          }
        } else {
          ErrorToast(response.data.error?.msg || "Failed to update");
        }
      } catch (err) {
        ErrorToast(err.response?.data?.error?.msg || "Something went wrong");
      }
    }
    setLoading(false);
  };

  submitRef.current = onSubmit;

  useEffect(() => {
    const onKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      const inTextarea = tag === "textarea";

      if (e.key === "F2") {
        e.preventDefault();
        if (!isGuest) setCustomerSelectorOpenTrigger((n) => n + 1);
        return;
      }
      if (e.key === "F4") {
        e.preventDefault();
        focusFirstItemCode();
        return;
      }
      if (e.key === "F1" && isGuest) {
        e.preventDefault();
        guestContactRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "F3" && isGuest) {
        e.preventDefault();
        guestAddressRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "F9") {
        e.preventDefault();
        discountRef.current?.focus({ preventScroll: true });
        return;
      }
      if (e.key === "F10") {
        e.preventDefault();
        paymentRef.current?.focus({ preventScroll: true });
        return;
      }
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !inTextarea
      ) {
        const inItemsTable = e.target?.closest?.("[data-invoice-items-table]");
        if (!inItemsTable) {
          const now = Date.now();
          const last = lastEnterAtRef.current;
          if (last && now - last <= DOUBLE_ENTER_MS) {
            e.preventDefault();
            lastEnterAtRef.current = 0;
            if (!loadingRef.current) {
              submitRef.current?.({ preventDefault: () => {} }, 1);
            }
            return;
          }
          lastEnterAtRef.current = now;
        } else {
          lastEnterAtRef.current = 0;
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !inTextarea) {
        e.preventDefault();
        submitRef.current?.(
          { preventDefault: () => {} },
          e.shiftKey ? 2 : 1
        );
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [focusFirstItemCode, isGuest]);

  const handleClear = () => {
    if (
      window.confirm(
        "Reload invoice from server? Unsaved changes will be lost."
      )
    ) {
      refreshInvoice();
    }
  };

  const itemsSubtitle = isGuest
    ? "Editing guest sale — scan or type item code"
    : SelectedCustomer
    ? `Editing ${SelectedCustomer.name} — type code, Enter, then Qty`
    : "Select customer, then edit items";

  if (!pageReady) {
    return (
      <div
        className="flex flex-col min-h-[calc(100vh-4rem)] font-quicksand -mx-2 sm:-mx-4"
        data-pos-invoice
      >
        <div className="flex-1 flex items-center justify-center p-8">
          <FetchingLoading />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col min-h-[calc(100vh-4rem)] font-quicksand -mx-2 sm:-mx-4"
      data-pos-invoice
    >
      <header className="shrink-0 border-b border-line bg-card shadow-soft">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 px-4 py-3 items-stretch">
          <div className="flex shrink-0 flex-row lg:flex-col gap-4 lg:gap-3 items-start justify-start border-b lg:border-b-0 lg:border-r border-line/80 pb-3 lg:pb-0 lg:pr-6">
            <div className="flex flex-col gap-0.5 items-start opacity-80 pointer-events-none select-none">
              <SaleType saleType={saleType} setSaleType={() => {}} />
              <span className="text-[10px] font-semibold text-muted hidden sm:block">
                Edit mode · Sale only
              </span>
            </div>
            <div className="flex flex-col gap-0.5 items-start opacity-80 pointer-events-none select-none">
              <InvoiceCustomerType
                customerType={invoiceCustomerType}
                setCustomerType={() => {}}
                label="Sale to"
              />
              <span className="text-[10px] font-semibold text-muted hidden sm:block">
                Party type locked
              </span>
            </div>
          </div>

          <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-3 gap-y-3 content-start">
            <PosField label={isGuest ? "Guest name" : "Customer"}>
              {isGuest ? (
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Required — guest name"
                  className={posInputClass}
                />
              ) : (
                <CustomerSelector
                  compact
                  SelectedCustomer={SelectedCustomer}
                  setSelectedCustomer={setSelectedCustomer}
                  openTrigger={customerSelectorOpenTrigger}
                  onCustomerSelected={() => {
                    requestAnimationFrame(() => {
                      setTimeout(focusInvoiceNoField, 0);
                    });
                  }}
                />
              )}
            </PosField>
            <InvoiceMetaFields
              invoiceNoInputRef={invoiceNoInputRef}
              invoiceNo={InvoiceNo}
              onInvoiceNoChange={setInvoiceNo}
              date={CurDate}
              onDateChange={setCurDate}
              description={Description}
              onDescriptionChange={setDescription}
              autoInvoiceNo={autoInvoiceNo}
              isGuest={isGuest}
            />
          </div>
        </div>

        {customerChanged && (
          <div className="mx-4 mb-3 flex gap-3 rounded-xl border border-warning/30 bg-warningSoft px-4 py-2.5 text-warning">
            <FiAlertCircle className="shrink-0 mt-0.5" size={18} />
            <p className="text-sm">
              <span className="font-bold">Customer changed.</span> Save will
              move this invoice balance to{" "}
              <strong>{SelectedCustomer.name}</strong>.
            </p>
          </div>
        )}
      </header>

      <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
        <section className="flex-1 min-w-0 flex flex-col min-h-[320px] lg:min-h-0 border-b lg:border-b-0 lg:border-r border-line bg-page">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-line bg-card/80 shrink-0">
            <FiPackage className="text-main shrink-0" size={16} />
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-ink">
                Line items
              </p>
              <p className="text-[11px] text-muted truncate">{itemsSubtitle}</p>
            </div>
            <span className="ml-auto text-xs font-bold tabular-nums text-main bg-main/10 px-2.5 py-1 rounded-full">
              {committedItems.length} item
              {committedItems.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="flex-1 min-h-0 overflow-auto p-3 sm:p-4 scrollable">
            <CreateInvoiceItemsTable
              items={itemRows}
              setItems={setItemRows}
              saleType={saleType}
              customerAccountType={
                isGuest ? undefined : SelectedCustomer?.type
              }
              editInvoiceId={routeInvoiceId}
              editCustomerId={isGuest ? undefined : SelectedCustomer?._id}
              editIsGuest={isGuest}
              onInvoiceMutated={refreshInvoice}
              skipStockValidation
              variant="pos"
            />
          </div>
        </section>

        <aside className="w-full lg:w-[min(100%,320px)] shrink-0 flex flex-col bg-card border-line">
          <div className="px-4 py-3 border-b border-line bg-subtle/60">
            <p className="text-xs font-bold uppercase tracking-widest text-muted">
              Bill summary
            </p>
          </div>

          <div className="flex-1 overflow-y-auto scrollable px-4 py-3 space-y-0.5">
            <SummaryLine label="Qty" value={fmt(totalQty)} />
            <SummaryLine label="Sale" value={`Rs ${fmt(billTotal)}`} />
            {shoppingTotal > 0 && (
              <SummaryLine
                label="Shopping"
                value={`Rs ${fmt(shoppingTotal)}`}
              />
            )}
            <SummaryLine label="Purchase" value={`Rs ${fmt(purchaseTotal)}`} />
            <SummaryLine
              label="Profit"
              value={`Rs ${fmt(profit)}`}
              highlight={profit >= 0}
            />
            <SummaryLine
              label="Net bill"
              value={`Rs ${fmt(netBill)}`}
              bold
              large
            />
            {!isGuest && SelectedCustomer && (
              <>
                <SummaryLine
                  label="Old balance"
                  value={`Rs ${fmt(oldBalanceExcludingInvoice)}`}
                />
                <SummaryLine
                  label="Remaining"
                  value={`Rs ${fmt(remaining)}`}
                  bold
                />
              </>
            )}
          </div>

          <div className="border-t border-line px-4 py-3 bg-subtle/30">
            <ThermalPrintModeSelect
              compact
              value={thermalPrintMode}
              onChange={setThermalPrintMode}
            />
          </div>
        </aside>
      </div>

      <footer className="shrink-0 sticky bottom-0 z-20 border-t border-line bg-card/95 backdrop-blur-sm px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-wrap items-end gap-3 min-w-0 flex-1">
            {isGuest && (
              <>
                <PosField label="Contact" className="w-[min(100%,150px)]">
                  <input
                    ref={guestContactRef}
                    type="text"
                    value={guestContact}
                    onChange={(e) => setGuestContact(e.target.value)}
                    placeholder="Phone"
                    title="F1"
                    className={posInputClass}
                  />
                </PosField>
                <PosField
                  label="Address"
                  className="min-w-[140px] flex-1 max-w-[280px]"
                >
                  <input
                    ref={guestAddressRef}
                    type="text"
                    value={guestAddress}
                    onChange={(e) => setGuestAddress(e.target.value)}
                    placeholder="Address"
                    title="F3"
                    className={posInputClass}
                  />
                </PosField>
              </>
            )}
            {!isGuest && SelectedCustomer && (
              <>
                <PosField label="Contact" className="w-[min(100%,150px)]">
                  <input
                    type="text"
                    value={guestContact}
                    onChange={(e) => setGuestContact(e.target.value)}
                    placeholder="Phone"
                    className={posInputClass}
                  />
                </PosField>
                <PosField
                  label="Address"
                  className="min-w-[140px] flex-1 max-w-[280px]"
                >
                  <input
                    type="text"
                    value={guestAddress}
                    onChange={(e) => setGuestAddress(e.target.value)}
                    placeholder="Address"
                    className={posInputClass}
                  />
                </PosField>
              </>
            )}
            <PosField label="Discount" className="w-[min(100%,140px)]">
              <input
                ref={discountRef}
                type="number"
                value={Discount}
                onChange={(e) => setDiscount(e.target.value)}
                placeholder="0"
                title="F9"
                className={posInputClass}
              />
            </PosField>
            <PosField
              label={isGuest ? "Cash payment" : "Payment"}
              className="w-[min(100%,140px)]"
            >
              <input
                ref={paymentRef}
                type="number"
                value={Payment}
                onChange={(e) => setPayment(e.target.value)}
                placeholder="0"
                title="F10"
                className={posInputClass}
              />
            </PosField>
            <p className="text-[11px] text-muted hidden lg:block pb-2.5">
              <FiUser className="inline mr-1 -mt-0.5" size={12} />
              <kbd className="px-1 py-0.5 rounded bg-subtle border border-line text-[10px] font-mono">
                F9
              </kbd>{" "}
              Discount ·{" "}
              <kbd className="px-1 py-0.5 rounded bg-subtle border border-line text-[10px] font-mono">
                F10
              </kbd>{" "}
              Payment
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={handleClear}
              disabled={Loading}
              title="Reload from server"
              className="inline-flex items-center gap-2 h-11 px-4 rounded-xl border-2 border-danger/30 bg-white text-danger text-sm font-bold hover:bg-dangerSoft disabled:opacity-50 transition-colors active:scale-[.98]"
            >
              <FiXCircle size={18} />
              Clear
            </button>
            <button
              type="button"
              onClick={(e) => onSubmit(e, 2)}
              disabled={Loading}
              title="Ctrl+Shift+Enter"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border-2 border-main bg-white text-main text-sm font-bold hover:bg-main/5 disabled:opacity-50 transition-colors"
            >
              <FiPrinter size={18} />
              Save &amp; print
            </button>
            <button
              type="button"
              onClick={(e) => onSubmit(e, 1)}
              disabled={Loading}
              title="Ctrl+Enter · Enter Enter"
              className="inline-flex items-center gap-2 h-11 px-6 rounded-xl bg-main text-white text-sm font-bold hover:bg-mainHover shadow-md disabled:opacity-50 transition-colors active:scale-[.98]"
            >
              <FiSend size={18} />
              Save invoice
            </button>
          </div>
        </div>
        {Loading && (
          <div className="mt-2">
            <FetchingLoading />
          </div>
        )}
      </footer>

      {thermalReceiptData && (
        <div
          aria-hidden
          className="fixed pointer-events-none opacity-0"
          style={{ left: -10000, top: 0 }}
        >
          <ThermalCustomerInvoice
            ref={thermalPrintRef}
            data={{ ...thermalReceiptData, printMode: thermalPrintMode }}
          />
        </div>
      )}
    </div>
  );
};

export default EditInvoice;
